/* ============================================
   ALnoor Theme — Core JavaScript
   Utilities, custom element base, section API
   ============================================ */

/* --- Debounce --- */
function debounce(fn, delay = 300) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/* --- Fetch helpers --- */
async function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/${type}`
    }
  };
}

async function fetchSection(url, sectionId) {
  const separator = url.includes('?') ? '&' : '?';
  const response = await fetch(`${url}${separator}section_id=${sectionId}`);
  if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
  const text = await response.text();
  return new DOMParser().parseFromString(text, 'text/html');
}

/* --- Cart API --- */
const CartAPI = {
  async get() {
    const res = await fetch('/cart.js');
    return res.json();
  },

  async add(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    return res.json();
  },

  async update(updates) {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return res.json();
  },

  async change(line, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    return res.json();
  }
};

/* --- Trap Focus (Accessibility) --- */
function trapFocus(container, elementToFocus) {
  const focusable = container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (elementToFocus) elementToFocus.focus();

  function handler(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  container.addEventListener('keydown', handler);
  return () => container.removeEventListener('keydown', handler);
}

/* --- Scroll Animation Observer --- */
function initScrollAnimations() {
  const reducedMotion = !window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
  const supportsObserver = 'IntersectionObserver' in window;

  if (reducedMotion || !supportsObserver) {
    document.querySelectorAll('.scroll-animate').forEach((el) => el.classList.add('is-visible'));
    return;
  }

  document.documentElement.classList.add('js-animations');

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
  );

  document.querySelectorAll('.scroll-animate').forEach((el) => observer.observe(el));
}

/* --- Format Money --- */
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = (cents / 100).toFixed(2);
  const moneyFormat = format || window.theme?.moneyFormat || '${{amount}}';
  return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, value)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','));
}

/* --- Publish/Subscribe Events --- */
const PubSub = {
  events: {},
  subscribe(event, callback) {
    if (!this.events[event]) this.events[event] = [];
    this.events[event].push(callback);
    return () => {
      this.events[event] = this.events[event].filter((cb) => cb !== callback);
    };
  },
  publish(event, data) {
    if (!this.events[event]) return;
    this.events[event].forEach((callback) => callback(data));
  }
};

/* --- Initialize on DOM Ready --- */
document.addEventListener('DOMContentLoaded', () => {
  initScrollAnimations();
});

/* --- Re-init after Shopify section render --- */
document.addEventListener('shopify:section:load', () => {
  initScrollAnimations();
});
